import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { IdbService } from '../idb/idb.service';
import {
  Observable,
  forkJoin,
  switchMap,
  concat,
  mergeMap,
  map,
  BehaviorSubject,
  zip,
  of,
  tap,
  delay,
  catchError,
  concatMap,
  retry,
  from,
  toArray,
  retryWhen,
  take,
  throwError,
  EMPTY,
} from 'rxjs';
import { PWAModel } from '../../interfaces/offline/pwa';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { JobAddress } from '../../interfaces/job-address/job-address';
import { SyncStatus } from '../../interfaces/offline/sync-status';
import { HttpErrorResponse } from '@angular/common/http';
import { Attachment } from '../../interfaces/attachments/attachment';
import { SnackbarService } from '../snackbar/snackbar.service';
import { CalendarNote } from '../../interfaces/calendar/calendar-note';
import { RetryInfo } from '../../interfaces/offline/retry-info';
import { OfflineUserSyncDownLog } from '../../interfaces/offline/offline-user-sync-down-log';
import { format, utcToZonedTime } from 'date-fns-tz';
import { ProdAppointment } from '../../interfaces/production/prod-appointment';
import { Appointment } from '../../interfaces/appointment/appointment';
import { ServiceAppointment } from '../../interfaces/service/service-appointment';
import { HelperService } from '../helper/helper.service';

@Injectable({
  providedIn: 'root',
})
export class SyncService {
  // Sync Status Observer
  private syncStatusSubject = new BehaviorSubject<SyncStatus>({
    syncId: 0,
    syncStatus: 'Not Synced',
    lastSynced: new Date(),
  });
  currentSyncStatus = this.syncStatusSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private idb: IdbService,
    private localStorage: LocalStorageService,
    private snackbar: SnackbarService,
    private helperService: HelperService
  ) {
    this.getSyncStatus();
  }

  ngOnInit() {}

  //#region General

  getCurrentSyncStatus(): SyncStatus {
    return this.syncStatusSubject.value;
  }

  getSyncStatus() {
    this.idb.getSyncStatus().subscribe((syncStatus) => {
      if (syncStatus) {
        this.syncStatusSubject.next(syncStatus);
      }
    });
  }

  reloadSyncStatus() {
    // Clear the syncStatusSubject then fetch the latest sync status from the IDB
    this.syncStatusSubject.next({
      syncId: 0,
      syncStatus: 'Not Synced',
      lastSynced: new Date(),
    });

    this.getSyncStatus();
  }

  updateSyncStatus(status: SyncStatus) {
    return this.idb.updateSyncStatus(status).pipe(
      tap(() => {
        console.log(`[${status.syncStatus}] Updating sync status`);
        this.syncStatusSubject.next(status);
      }),
      delay(2000),
      tap(() => {
        console.log(
          `[${
            status.syncStatus
          }] Delay complete after 2000ms at ${new Date().toISOString()}`
        );
      })
    );
  }

  clearAllStores(): Observable<void[]> {
    const clearTasks$ = zip(
      this.idb.clearSalesCalendarEvents(),
      this.idb.clearCustomers(),
      this.idb.clearJobAddresses(),
      this.idb.clearAppointments(),
      this.idb.clearLeads(),
      this.idb.clearCustomerVisits(),
      this.idb.clearProposals(),
      this.idb.clearProducts(),
      this.idb.clearStatements(),
      this.idb.clearContracts(),
      this.idb.clearNotes(),
      this.idb.clearAttachments(),
      this.idb.clearGeneratedContracts(),
      this.idb.clearSoldSheets(),
      this.idb.clearProdAvailability(),
      this.idb.clearProdAppointments(),
      this.idb.clearProjects(),
      this.idb.clearProductionVisits(),
      this.idb.clearChangeOrders(),
      this.idb.clearUpsells(),
      this.idb.clearWrapUps(),
      this.idb.clearServiceAvailability(),
      this.idb.clearServiceAppointments(),
      this.idb.clearServiceProjects(),
      this.idb.clearServiceVisits(),
      this.idb.clearGeoZonesFull(),
      this.idb.clearAccountingInfo(),
      this.idb.clearInvoices(),
      this.idb.clearUpsellContracts(),
      this.idb.clearGeneratedUpsellContracts(),
      this.idb.clearUpsellAppointments(),
      this.idb.clearUpsellCalendarEvents(),
      this.idb.clearUpsellVisits(),
      this.idb.clearUpsellProducts(),
      this.idb.clearChangeOrderContracts(),
      this.idb.clearGeneratedChangeOrderContracts(),
      this.idb.clearCalendarNotes(),
      this.idb.clearBillingStatements(),
      this.idb.clearFollowUps(),
      this.idb.clearContactMethods(),
      this.idb.clearNoBidReasons(),
      this.idb.clearDispositions(),
      this.idb.clearSubDispositions(),
      this.idb.clearMailingRequests(),
      this.idb.clearDrawings(),
      this.idb.clearProdDailies(),
      this.idb.clearTimeLogs(),
      this.idb.clearTimeBreakDowns(),
      this.idb.clearSpectrumCrewMembers(),
      this.idb.clearCrews(),
      this.idb.clearSpectrumMaterials(),
      this.idb.clearDailyMaterials(),
      this.idb.clearDailyPaymentInfo(),
      this.idb.clearDailyPrompts(),
      this.idb.clearGeneratedInvoices(),
      this.idb.clearProductPricing(),
      this.idb.clearPendingPWAData(),
      this.idb.clearVehicleDocumentationAttachments()
    );

    return clearTasks$;
  }

  postOfflineUserSyncDownLog() {
    const syncLog: OfflineUserSyncDownLog = {
      userId: this.localStorage.get('uid'),
      userName: this.localStorage.get('name'),
      userEmail: this.localStorage.get('email'),
      userRole: this.localStorage.get('role'),
      mostRecentSyncDown: new Date(),
    };

    return this.apiService.postOfflineUserSyncDownLog(syncLog).pipe(
      tap(() => {}),
      catchError((error) => {
        console.error('Error posting sync down log:', error);
        return EMPTY;
      })
    );
  }

  //#endregion

  //#region Inside Sales
  clearInisdeSalesStores(): Observable<void[]> {
    const clearTasks$ = zip(
      this.idb.clearStatements(),
      this.idb.clearDispositions(),
      this.idb.clearSubDispositions()
    );

    return clearTasks$;
  }

  insideSalesSyncOnStartUp(): Observable<any> {
    const syncTasks$ = zip(
      this.syncInsideSalesStatements().pipe(retry(1)),
      this.syncInsideSalesDispositions().pipe(retry(1)),
      this.syncInsideSalesSubDispositions().pipe(retry(1)),
      this.syncInsideSalesNoBidReasons().pipe(retry(1))
      // this.syncDispositions().pipe(retry(1)),
      // this.syncSubDispositions().pipe(retry(1)),
      // this.syncNoBidReasons().pipe(retry(1))
    );

    return syncTasks$;
  }
  //#endregion

  //#region Sales
  salesSyncOnStartUp(): Observable<any> {
    const userId: string = this.localStorage.get('uid');

    const syncTasks$ = zip(
      this.syncSalesCalendar(userId).pipe(retry(1)),
      this.syncCustomers(userId).pipe(retry(1)),
      this.syncSalesAppointments(userId).pipe(retry(1)),
      this.syncJobAddresses(userId).pipe(retry(1)),
      this.syncLeads(userId).pipe(retry(1)),
      this.syncCustomerVisits(userId).pipe(retry(1)),
      this.syncProposals(userId).pipe(retry(1)),
      this.syncProducts(userId, 'Sales').pipe(retry(1)),
      this.syncStatements(userId, 'Sales').pipe(retry(1)),
      this.syncContracts(userId).pipe(retry(1)),
      this.syncGeneratedContracts(userId).pipe(retry(1)),
      this.syncNotes(userId).pipe(retry(1)),
      this.syncSoldSheets(userId).pipe(retry(1)),
      this.syncAllGeoZones().pipe(retry(1)),
      this.syncCalendarNotes(userId, 'Sales').pipe(retry(1)),
      this.syncFollowUps(userId).pipe(retry(1)),
      this.syncSalesContactMethods(userId).pipe(retry(1)),
      this.syncNoBidReasons(userId, 'Sales').pipe(retry(1)),
      this.syncDispositions(userId, 'Sales').pipe(retry(1)),
      this.syncSubDispositions(userId, 'Sales').pipe(retry(1)),
      this.syncMailingRequests(userId, 'Sales').pipe(retry(1)),
      this.syncDrawings(userId, 'Sales').pipe(retry(1)),
      this.syncProductPricing(userId, 'Sales').pipe(retry(1))
    );

    const hotQuoteSyncTasks$ = zip(
      this.syncSalesCalendar(userId, true).pipe(retry(1)),
      this.syncCustomers(userId, true).pipe(retry(1)),
      this.syncSalesAppointments(userId, true).pipe(retry(1)),
      this.syncJobAddresses(userId, true).pipe(retry(1)),
      this.syncLeads(userId, true).pipe(retry(1)),
      this.syncCustomerVisits(userId, true).pipe(retry(1)),
      this.syncProposals(userId, true).pipe(retry(1)),
      this.syncContracts(userId, true).pipe(retry(1)),
      this.syncGeneratedContracts(userId, true).pipe(retry(1)),
      this.syncNotes(userId, true).pipe(retry(1)),
      this.syncSoldSheets(userId, true).pipe(retry(1)),
      this.syncFollowUps(userId, true).pipe(retry(1)),
      this.syncSalesContactMethods(userId, true).pipe(retry(1)),
      this.syncMailingRequests(userId, 'Sales', true).pipe(retry(1)),
      this.syncDrawings(userId, 'Sales', true).pipe(retry(1))
    );

    const updateStatus$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Attachments',
      lastSynced: new Date(),
    });

    const jobAddressIds$ = this.apiService.getPWAJobAddressIdsForAttachments(
      userId,
      'Sales'
    );

    const saveAttachments$ = jobAddressIds$.pipe(
      concatMap((jobAddressIds) => {
        return from(jobAddressIds).pipe(
          concatMap((jobAddressId) => {
            console.log('jobAddressId', jobAddressId);
            return this.apiService
              .getPWAAttachmentsByJobAddressId(jobAddressId)
              .pipe(
                retry(2),
                concatMap((attachments) => {
                  console.log('attachments', attachments);
                  return from(attachments).pipe(
                    concatMap((attachment) => {
                      return this.idb.addAttachment(attachment, true);
                    })
                  );
                }),
                catchError((error) => {
                  console.error(
                    `Failed to get attachments for job address ID ${jobAddressId}:`,
                    error
                  );
                  return of(0);
                })
              );
          })
        );
      })
    );

    const postOfflineUserSyncDownLog$ = this.postOfflineUserSyncDownLog();

    let concat$ = concat(
      syncTasks$,
      hotQuoteSyncTasks$,
      updateStatus$,
      saveAttachments$,
      postOfflineUserSyncDownLog$
    );

    return concat$;
  }

  salesSyncDownAfterUpload(): Observable<any> {
    return concat(
      this.clearAllStores(),
      this.updateSyncStatus({
        syncId: 1,
        syncStatus: 'Syncing Down',
        lastSynced: new Date(),
      }),
      this.salesSyncOnStartUp(),
      this.updateSyncStatus({
        syncId: 1,
        syncStatus: 'Synced Down',
        lastSynced: new Date(),
      })
    );
  }

  buildSalesPWAModel(jobAddress: JobAddress): Observable<PWAModel> {
    const attachments = this.idb
      .getAttachmentsByJobAddressId(jobAddress.jobAddressId!)
      .pipe(
        map((attachments) =>
          attachments.map((attachment) => {
            attachment.attachmentBase64String = '';
            attachment.attachmentByteArray = undefined;
            return attachment;
          })
        )
      );

    return zip(
      this.idb.getCustomerById(Number(jobAddress.customerId)),
      this.idb.getLeadsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getCustomerVisitsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getProposalsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getNewNotesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getSoldSheetsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getFollowUpsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getProjectsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getContactMethodsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getMailingRequestsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getDrawingsByJobAddressId(jobAddress.jobAddressId!, true),
      this.idb.getInvoicesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedInvoicesByJobAddressId(jobAddress.jobAddressId!),
      attachments
    ).pipe(
      switchMap((data) => {
        const pwaModel: PWAModel = {
          syncRecordId:
            crypto.randomUUID() + this.localStorage.get('employeeCode'),
          userId: this.localStorage.get('uid'),
          jobAddressId: jobAddress.jobAddressId!,
          jobAddress: jobAddress,
          customer: data[0],
          leads: data[1] ? data[1] : [],
          customerVisits: data[2] ? data[2] : [],
          proposals: data[3] ? data[3] : [],
          contracts: data[4] ? data[4] : [],
          notes: data[5] ? data[5] : [],
          generatedContract: data[6] ? data[6] : [],
          soldSheets: data[7] ? data[7] : [],
          followUps: data[8] ? data[8] : [],
          projects: data[9] ? data[9] : [],
          contactMethods: data[10] ? data[10] : [],
          mailingRequests: data[11] ? data[11] : [],
          drawings: data[12] ? data[12] : [],
          invoices: data[13] ? data[13] : [],
          generatedInvoices: data[14] ? data[14] : [],
          attachments: data[15] ? data[15] : [],
          createdDate: new Date(),
        };
        return of(pwaModel);
      })
    );
  }

  salesSyncDataToServer(): Observable<any> {
    const initialStatusUpdate$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Up',
      lastSynced: new Date(),
    });

    const jobAddresses$ = this.idb.getAllModifiedJobAddresses();

    const pwaModels: PWAModel[] = [];

    const pwaModels$ = jobAddresses$.pipe(
      mergeMap((jobAddresses) => {
        return forkJoin(
          jobAddresses.map((jobAddress) => {
            return this.buildSalesPWAModel(jobAddress).pipe(
              tap((pwaModel) => {
                console.log(
                  'PWA Model built for JobAddressId:',
                  jobAddress.jobAddressId,
                  pwaModel
                );
                pwaModels.push(pwaModel);
              })
            );
          })
        );
      })
    );

    let retryCount = 0;
    const postPwaModels$ = pwaModels$.pipe(
      concatMap(() => {
        return this.idb.getAllPendingPWAData().pipe(
          switchMap((pendingUploads) => {
            const modelsToUpload =
              pendingUploads.length > 0 ? pendingUploads : pwaModels;

            return from(modelsToUpload).pipe(
              concatMap((pwaModel) => {
                retryCount = 0;

                // Always add to pending store before sending (if not already there)
                return this.idb.addPendingPWAData(pwaModel).pipe(
                  switchMap(() =>
                    this.apiService.postSalesPWAData(pwaModel).pipe(
                      tap(() => {
                        // Upload succeeded, remove from pending
                        this.idb
                          .getPendingPWADataByJobAddressId(
                            pwaModel.jobAddressId
                          )
                          .subscribe({
                            next: (pendingData) => {
                              if (pendingData) {
                                this.idb
                                  .removePendingPWAData(
                                    pendingData.pwaBackUpId!
                                  )
                                  .subscribe();
                              }
                            },
                            error: (err) => {
                              console.error(
                                'Error removing pending PWA data:',
                                err
                              );
                            },
                          });
                      }),
                      catchError(
                        (err: HttpErrorResponse, caught: Observable<any>) => {
                          console.error('Upload failed', err);

                          let retryInfo: RetryInfo = {
                            retryCount,
                            retryError: JSON.stringify(err),
                            retryTimeStamp: new Date(),
                          };
                          pwaModel.retryInfo = retryInfo;

                          if (
                            err.error?.detail ===
                            'Duplicate Sync Record Detected.'
                          ) {
                            console.log('Duplicate Sync Record Detected.');
                            return of(this.idb.addPWABackUpData(pwaModel));
                          }

                          if (
                            String(err.error?.detail).includes('deadlocked')
                          ) {
                            if (retryCount === 0) {
                              retryCount++;
                              console.log('Deadlock detected. Retrying...');
                              return caught.pipe(retry(1));
                            }
                          }

                          // Log failed model to backup
                          return of(this.idb.addPWABackUpData(pwaModel));
                        }
                      )
                    )
                  )
                );
              }),
              toArray()
            );
          })
        );
      })
    );

    const calendarNotes$ = this.idb.getAllModifiedCalendarNotes();

    const calendarNotesPost$ = calendarNotes$.pipe(
      concatMap((calendarNotes: CalendarNote[]) => {
        return from(calendarNotes).pipe(
          concatMap((calendarNote: CalendarNote) => {
            return this.apiService.postPWACalendarNote(calendarNote).pipe(
              retry(1),
              catchError((error) => {
                console.error('Error posting calendar note:', error);
                return of(error);
              })
            );
          })
        );
      })
    );

    const syncAttachmentsStatus$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Attachments',
      lastSynced: new Date(),
    });

    let concat$ = concat(
      initialStatusUpdate$,
      // pwaModels$,
      postPwaModels$,
      calendarNotesPost$,
      syncAttachmentsStatus$
    );

    return concat$;
  }

  getPWAAttachmentsAsFiles(): Observable<File[][]> {
    const jobAddressIds$ = this.idb.getAllJobAddressIds();

    const test$ = jobAddressIds$.pipe(
      concatMap((jobAddressIds) => {
        return forkJoin(
          jobAddressIds.map((jobAddressId) => {
            return this.attachmentTasksPerJobAddress(jobAddressId);
          })
        );
      })
    );

    return test$;
  }

  attachmentTasksPerJobAddress(jobAddressId: number) {
    const attachments$ = this.idb.getNewAttachmentsByJobAddressId(jobAddressId);

    const files$ = attachments$.pipe(
      concatMap((attachments) => {
        if (attachments.length === 0) return of([]);
        return forkJoin(
          attachments.map((attachment) => {
            return this.convertAttachmentToFile(attachment).pipe(
              catchError((error) => {
                console.error(
                  `Failed to convert attachment ${attachment.attachmentId} to file:`,
                  error
                );
                return EMPTY;
              })
            );
          })
        );
      })
    );

    const postFiles$ = files$.pipe();

    return files$;
  }

  convertAttachmentToFile(attachment: Attachment): Observable<File> {
    try {
      const byteCharacters = atob(attachment.attachmentBase64String!);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.attachmentType });
      let functionId = attachment.functionId ? attachment.functionId : '';
      let functionTable = attachment.functionTable
        ? attachment.functionTable
        : '';
      let attachmentLabel = attachment.attachmentLabel
        ? attachment.attachmentLabel
        : '';
      const file = new File(
        [blob],
        `${attachment.jobAddressId}!${attachment.customerId}!${functionId}!${functionTable}!${attachmentLabel}!@${attachment.attachmentName}`,
        {
          type: attachment.attachmentType,
        }
      );
      return of(file);
    } catch (error) {
      return EMPTY;
    }
  }

  //#endregion

  //#region Production
  prodSyncOnStartUp(): Observable<any> {
    const userId: string = this.localStorage.get('uid');

    const syncTasks$ = zip(
      this.syncProdCustomers(userId).pipe(retry(1)),
      this.syncProdJobAddresses(userId).pipe(retry(1)),
      this.syncProdAppointments(userId).pipe(retry(1)),
      this.syncProdAvailability(userId).pipe(retry(1)),
      this.syncProdGeneratedContracts(userId).pipe(retry(1)),
      this.syncProdProjects(userId).pipe(retry(1)),
      this.syncProdProduction(userId).pipe(retry(1)),
      this.syncProducts(userId, 'Production').pipe(retry(1)),
      this.syncStatements(userId, 'Production').pipe(retry(1)),
      this.syncProdNotes(userId).pipe(retry(1)),
      this.syncProdProposals(userId).pipe(retry(1)),
      this.syncProdChangeOrders(userId).pipe(retry(1)),
      this.syncProdAccountingInfo(userId).pipe(retry(1)),
      this.syncProdContracts(userId).pipe(retry(1)),
      this.syncProdInvoices(userId).pipe(retry(1)),
      this.syncAllGeoZones().pipe(retry(1)),
      this.syncUpsellProducts(userId, 'Production').pipe(retry(1)),
      this.syncChangeOrderContracts(userId).pipe(retry(1)),
      this.syncGeneratedChangeOrderContracts(userId).pipe(retry(1)),
      this.syncCalendarNotes(userId, 'Production').pipe(retry(1)),
      this.syncProdContactMethods(userId).pipe(retry(1)),
      this.syncMailingRequests(userId, 'Production').pipe(retry(1)),
      this.syncDrawings(userId, 'Production').pipe(retry(1)),
      this.syncSpectrumCrewMembers().pipe(retry(1)),
      this.syncSpectrumMaterials().pipe(retry(1)),
      this.syncProdDailies(userId).pipe(retry(1)),
      this.syncCrews(userId).pipe(retry(1)),
      this.syncTimeLogs(userId).pipe(retry(1)),
      this.syncTimeBreakDowns(userId).pipe(retry(1)),
      this.syncDailyMaterials(userId).pipe(retry(1)),
      this.syncDailyPaymentInfo(userId).pipe(retry(1)),
      this.syncDailyPrompts(userId).pipe(retry(1)),
      this.syncProdPWAUpsells(userId).pipe(retry(1)),
      this.syncProdPWAUpsellContracts(userId).pipe(retry(1)),
      this.syncProdPWAGeneratedUpsellContracts(userId).pipe(retry(1)),
      this.syncGeneratedInvoices(userId).pipe(retry(1)),
      this.syncBillingStatements(userId).pipe(retry(1)),
      this.syncProdLeads(userId).pipe(retry(1)),
      this.syncProductPricing(userId, 'Production').pipe(retry(1))
    );

    const updateStatus$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Attachments',
      lastSynced: new Date(),
    });

    const jobAddressIds$ = this.apiService.getPWAJobAddressIdsForAttachments(
      userId,
      'Production'
    );

    const saveAttachments$ = jobAddressIds$.pipe(
      concatMap((jobAddressIds) => {
        return from(jobAddressIds).pipe(
          concatMap((jobAddressId) => {
            console.log('jobAddressId', jobAddressId);
            return this.apiService
              .getPWAAttachmentsByJobAddressId(jobAddressId)
              .pipe(
                retry(2),
                concatMap((attachments) => {
                  console.log('attachments', attachments);
                  return from(attachments).pipe(
                    concatMap((attachment) => {
                      return this.idb.addAttachment(attachment, true);
                    })
                  );
                }),
                catchError((error) => {
                  console.error(
                    `Failed to get attachments for job address ID ${jobAddressId}:`,
                    error
                  );
                  return of(0);
                })
              );
          })
        );
      })
    );

    const saveVehicleDocumentationAttachments$ = this.apiService
      .getAllVehicleDocumentationAttachments()
      .pipe(
        retry(2),
        concatMap((attachments) => {
          return from(attachments).pipe(
            concatMap((attachment) => {
              return this.idb.addVehicleDocumentationAttachment(attachment);
            })
          );
        })
      );

    const postOfflineUserSyncDownLog$ = this.postOfflineUserSyncDownLog();

    let concat$ = concat(
      syncTasks$,
      updateStatus$,
      saveAttachments$,
      saveVehicleDocumentationAttachments$,
      postOfflineUserSyncDownLog$
    );

    return concat$;
  }

  prodSyncDownAfterUpload(): Observable<any> {
    return concat(
      this.clearAllStores(),
      this.updateSyncStatus({
        syncId: 1,
        syncStatus: 'Syncing Down',
        lastSynced: new Date(),
      }),
      this.prodSyncOnStartUp(),
      this.updateSyncStatus({
        syncId: 1,
        syncStatus: 'Synced Down',
        lastSynced: new Date(),
      })
    );
  }

  prodBuildPWAModel(jobAddress: JobAddress): Observable<PWAModel> {
    const attachments = this.idb
      .getAttachmentsByJobAddressId(jobAddress.jobAddressId!)
      .pipe(
        map((attachments) =>
          attachments.map((attachment) => {
            attachment.attachmentBase64String = '';
            attachment.attachmentByteArray = undefined;
            return attachment;
          })
        )
      );

    return zip(
      this.idb.getCustomerById(Number(jobAddress.customerId)),
      this.idb.getProductionByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getNewNotesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getWrapUpsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getChangeOrdersByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getInvoicesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getUpsellsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getUpsellContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedUpsellContractsByJobAddressId(
        jobAddress.jobAddressId!
      ),
      this.idb.getChangeOrderContractByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedChangeOrderContractByJobAddressId(
        jobAddress.jobAddressId!
      ),
      this.idb.getBillingStatementsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getProjectsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getContactMethodsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getMailingRequestsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getDrawingsByJobAddressId(jobAddress.jobAddressId!, true),
      this.idb.getProdDailiesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getCrewsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getTimeLogsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getTimeBreakDownsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getDailyMaterialsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getProposalsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getDailyPaymentInfoByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedInvoicesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getDailyPromptsByJobAddressId(jobAddress.jobAddressId!),
      attachments
    ).pipe(
      switchMap((data) => {
        const pwaModel: PWAModel = {
          syncRecordId:
            crypto.randomUUID() + this.localStorage.get('employeeCode'),
          userId: this.localStorage.get('uid'),
          jobAddressId: jobAddress.jobAddressId!,
          jobAddress: jobAddress,
          customer: data[0],
          production: data[1] ? data[1] : [],
          notes: data[2] ? data[2] : [],
          wrapUps: data[3] ? data[3] : [],
          changeOrders: data[4] ? data[4] : [],
          invoices: data[5] ? data[5] : [],
          upsells: data[6] ? data[6] : [],
          upsellContracts: data[7] ? data[7] : [],
          generatedUpsellContracts: data[8] ? data[8] : [],
          changeOrderContracts: data[9] ? data[9] : [],
          generatedChangeOrderContracts: data[10] ? data[10] : [],
          billingStatements: data[11] ? data[11] : [],
          projects: data[12] ? data[12] : [],
          contactMethods: data[13] ? data[13] : [],
          mailingRequests: data[14] ? data[14] : [],
          drawings: data[15] ? data[15] : [],
          prodDailies: data[16] ? data[16] : [],
          crews: data[17] ? data[17] : [],
          timeLogs: data[18] ? data[18] : [],
          timeBreakDowns: data[19] ? data[19] : [],
          dailyMaterials: data[20] ? data[20] : [],
          proposals: data[21] ? data[21] : [],
          contracts: data[22] ? data[22] : [],
          generatedContract: data[23] ? data[23] : [],
          dailyPaymentInfo: data[24] ? data[24] : [],
          generatedInvoices: data[25] ? data[25] : [],
          dailyPrompts: data[26] ? data[26] : [],
          attachments: data[27] ? data[27] : [],
          createdDate: new Date(),
        };
        return of(pwaModel);
      })
    );
  }

  prodSyncDataToServer(): Observable<any> {
    console.log('prodSyncDataToServer called');
    const initialStatusUpdate$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Up',
      lastSynced: new Date(),
    });

    const jobAddresses$ = this.idb.getAllModifiedJobAddresses();

    const pwaModels: PWAModel[] = [];

    const pwaModels$ = jobAddresses$.pipe(
      mergeMap((jobAddresses) => {
        return forkJoin(
          jobAddresses.map((jobAddress) => {
            return this.prodBuildPWAModel(jobAddress).pipe(
              tap((pwaModel) => {
                console.log(
                  'PWA Model built for JobAddressId:',
                  jobAddress.jobAddressId,
                  pwaModel
                );
                pwaModels.push(pwaModel);
              })
            );
          })
        );
      })
    );

    let retryCount = 0;
    const postPwaModels$ = pwaModels$.pipe(
      concatMap(() => {
        return this.idb.getAllPendingPWAData().pipe(
          switchMap((pendingUploads) => {
            console.log('Pending uploads:', pendingUploads);
            const modelsToUpload =
              pendingUploads.length > 0 ? pendingUploads : pwaModels;

            return from(modelsToUpload).pipe(
              concatMap((pwaModel) => {
                retryCount = 0;

                // Always add to pending store before sending (if not already there)
                return this.idb.addPendingPWAData(pwaModel).pipe(
                  switchMap(() =>
                    this.apiService.postProdPWAData(pwaModel).pipe(
                      tap(() => {
                        // Upload succeeded, remove from pending
                        this.idb
                          .getPendingPWADataByJobAddressId(
                            pwaModel.jobAddressId
                          )
                          .subscribe({
                            next: (pendingData) => {
                              if (pendingData) {
                                this.idb
                                  .removePendingPWAData(
                                    pendingData.pwaBackUpId!
                                  )
                                  .subscribe();
                              }
                            },
                            error: (err) => {
                              console.error(
                                'Error removing pending PWA data:',
                                err
                              );
                            },
                          });
                      }),
                      catchError(
                        (err: HttpErrorResponse, caught: Observable<any>) => {
                          console.error('Upload failed', err);

                          let retryInfo: RetryInfo = {
                            retryCount,
                            retryError: JSON.stringify(err),
                            retryTimeStamp: new Date(),
                          };
                          pwaModel.retryInfo = retryInfo;

                          if (
                            err.error?.detail ===
                            'Duplicate Sync Record Detected.'
                          ) {
                            console.log('Duplicate Sync Record Detected.');
                            return of(this.idb.addPWABackUpData(pwaModel));
                          }

                          if (
                            String(err.error?.detail).includes('deadlocked')
                          ) {
                            if (retryCount === 0) {
                              retryCount++;
                              console.log('Deadlock detected. Retrying...');
                              return caught.pipe(retry(1));
                            }
                          }

                          // Log failed model to backup
                          return of(this.idb.addPWABackUpData(pwaModel));
                        }
                      )
                    )
                  )
                );
              }),
              toArray()
            );
          })
        );
      })
    );

    const calendarNotes$ = this.idb.getAllModifiedCalendarNotes();

    const calendarNotesPost$ = calendarNotes$.pipe(
      concatMap((calendarNotes: CalendarNote[]) => {
        // console.log('calendar notes', calendarNotes);
        return from(calendarNotes).pipe(
          concatMap((calendarNote: CalendarNote) => {
            return this.apiService.postPWACalendarNote(calendarNote).pipe(
              retry(1),
              catchError((error) => {
                console.error('Error posting calendar note:', error);
                return of(error);
              })
            );
          })
        );
      })
    );

    const syncAttachmentsStatus$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Attachments',
      lastSynced: new Date(),
    });

    let concat$ = concat(
      initialStatusUpdate$,
      // pwaModels$,
      postPwaModels$,
      calendarNotesPost$,
      syncAttachmentsStatus$
    );

    return concat$;
  }

  //#endregion

  //#region Service
  serviceSyncOnStartUp(): Observable<any> {
    const userId: string = this.localStorage.get('uid');
    const syncTasks$ = zip(
      this.syncServiceCustomers(userId).pipe(retry(1)),
      this.syncServiceJobAddresses(userId).pipe(retry(1)),
      this.syncServiceAppointments(userId).pipe(retry(1)),
      this.syncServiceAvailability(userId).pipe(retry(1)),
      this.syncServiceGeneratedContracts(userId).pipe(retry(1)),
      this.syncProducts(userId, 'Service').pipe(retry(1)),
      this.syncStatements(userId, 'Service').pipe(retry(1)),
      this.syncServiceNotes(userId).pipe(retry(1)),
      this.syncServiceProjects(userId).pipe(retry(1)),
      this.syncServiceVisits(userId).pipe(retry(1)),
      this.syncServiceProposals(userId).pipe(retry(1)),
      this.syncServiceProdProjects(userId).pipe(retry(1)),
      this.syncServiceProdWrapUps(userId).pipe(retry(1)),
      this.syncServiceProdVisits(userId).pipe(retry(1)),
      this.syncAllGeoZones().pipe(retry(1)),
      this.syncUpsellProducts(userId, 'Service').pipe(retry(1)),
      this.syncCalendarNotes(userId, 'Service').pipe(retry(1)),
      this.syncServiceContactMethods(userId).pipe(retry(1)),
      this.syncServiceSoldSheets(userId).pipe(retry(1)),
      this.syncServiceContracts(userId).pipe(retry(1)),
      this.syncMailingRequests(userId, 'Service').pipe(retry(1)),
      this.syncDrawings(userId, 'Service').pipe(retry(1)),
      this.syncProductPricing(userId, 'Service').pipe(retry(1))
    );

    const updateStatus$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Attachments',
      lastSynced: new Date(),
    });

    const jobAddressIds$ = this.apiService.getPWAJobAddressIdsForAttachments(
      userId,
      'Service'
    );

    const saveAttachments$ = jobAddressIds$.pipe(
      concatMap((jobAddressIds) => {
        return from(jobAddressIds).pipe(
          concatMap((jobAddressId) => {
            console.log('jobAddressId', jobAddressId);
            return this.apiService
              .getPWAAttachmentsByJobAddressId(jobAddressId)
              .pipe(
                retry(2),
                concatMap((attachments) => {
                  console.log('attachments', attachments);
                  return from(attachments).pipe(
                    concatMap((attachment) => {
                      return this.idb.addAttachment(attachment, true);
                    })
                  );
                }),
                catchError((error) => {
                  console.error(
                    `Failed to get attachments for job address ID ${jobAddressId}:`,
                    error
                  );
                  return of(0);
                })
              );
          })
        );
      })
    );

    const saveVehicleDocumentationAttachments$ = this.apiService
      .getAllVehicleDocumentationAttachments()
      .pipe(
        retry(2),
        concatMap((attachments) => {
          return from(attachments).pipe(
            concatMap((attachment) => {
              return this.idb.addVehicleDocumentationAttachment(attachment);
            })
          );
        })
      );

    const postOfflineUserSyncDownLog$ = this.postOfflineUserSyncDownLog();

    let concat$ = concat(
      syncTasks$,
      updateStatus$,
      saveAttachments$,
      saveVehicleDocumentationAttachments$,
      postOfflineUserSyncDownLog$
    );

    return concat$;
  }

  serviceSyncDownAfterUpload(): Observable<any> {
    return concat(
      this.clearAllStores(),
      this.updateSyncStatus({
        syncId: 1,
        syncStatus: 'Syncing Down',
        lastSynced: new Date(),
      }),
      this.serviceSyncOnStartUp(),
      this.updateSyncStatus({
        syncId: 1,
        syncStatus: 'Synced Down',
        lastSynced: new Date(),
      })
    );
  }

  serviceBuildPWAModel(jobAddress: JobAddress): Observable<PWAModel> {
    const attachments = this.idb
      .getAttachmentsByJobAddressId(jobAddress.jobAddressId!)
      .pipe(
        map((attachments) =>
          attachments.map((attachment) => {
            attachment.attachmentBase64String = '';
            attachment.attachmentByteArray = undefined;
            return attachment;
          })
        )
      );

    return zip(
      this.idb.getCustomerById(Number(jobAddress.customerId)),
      this.idb.getNewNotesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getWrapUpsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getServiceVisitsByJobAddressId(jobAddress.jobAddressId!),
      // this.idb.getChangeOrdersByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getInvoicesByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getUpsellsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getUpsellVisitsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getUpsellContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getGeneratedUpsellContractsByJobAddressId(
        jobAddress.jobAddressId!
      ),
      this.idb.getUpsellAppointmentsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getProposalsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getNewGeneratedContractsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getProjectsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getContactMethodsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getSoldSheetsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getMailingRequestsByJobAddressId(jobAddress.jobAddressId!),
      this.idb.getDrawingsByJobAddressId(jobAddress.jobAddressId!, true),
      this.idb.getGeneratedInvoicesByJobAddressId(jobAddress.jobAddressId!),
      attachments
    ).pipe(
      switchMap((data) => {
        const pwaModel: PWAModel = {
          syncRecordId:
            crypto.randomUUID() + this.localStorage.get('employeeCode'),
          userId: this.localStorage.get('uid'),
          jobAddressId: jobAddress.jobAddressId!,
          jobAddress: jobAddress,
          customer: data[0],
          notes: data[1] ? data[1] : [],
          wrapUps: data[2] ? data[2] : [],
          serviceVisits: data[3] ? data[3] : [],
          // changeOrders: data[4] ? data[4] : [],
          invoices: data[4] ? data[4] : [],
          upsells: data[5] ? data[5] : [],
          upsellVisits: data[6] ? data[6] : [],
          upsellContracts: data[7] ? data[7] : [],
          generatedUpsellContracts: data[8] ? data[8] : [],
          upsellAppointments: data[9] ? data[9] : [],
          proposals: data[10] ? data[10] : [],
          contracts: data[11] ? data[11] : [],
          generatedContract: data[12] ? data[12] : [],
          projects: data[13] ? data[13] : [],
          contactMethods: data[14] ? data[14] : [],
          soldSheets: data[15] ? data[15] : [],
          mailingRequests: data[16] ? data[16] : [],
          drawings: data[17] ? data[17] : [],
          generatedInvoices: data[18] ? data[18] : [],
          attachments: data[19] ? data[19] : [],
          createdDate: new Date(),
        };
        return of(pwaModel);
      })
    );
  }

  serviceSyncDataToServer(): Observable<any> {
    const initialStatusUpdate$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Up',
      lastSynced: new Date(),
    });

    const jobAddresses$ = this.idb.getAllModifiedJobAddresses();

    const pwaModels: PWAModel[] = [];

    const pwaModels$ = jobAddresses$.pipe(
      mergeMap((jobAddresses) => {
        return forkJoin(
          jobAddresses.map((jobAddress) => {
            return this.serviceBuildPWAModel(jobAddress).pipe(
              tap((pwaModel) => {
                console.log(
                  'PWA Model built for JobAddressId:',
                  jobAddress.jobAddressId,
                  pwaModel
                );
                pwaModels.push(pwaModel);
              })
            );
          })
        );
      })
    );

    let retryCount = 0;
    const postPwaModels$ = pwaModels$.pipe(
      concatMap(() => {
        return this.idb.getAllPendingPWAData().pipe(
          switchMap((pendingUploads) => {
            const modelsToUpload =
              pendingUploads.length > 0 ? pendingUploads : pwaModels;

            return from(modelsToUpload).pipe(
              concatMap((pwaModel) => {
                retryCount = 0;

                // Always add to pending store before sending (if not already there)
                return this.idb.addPendingPWAData(pwaModel).pipe(
                  switchMap(() =>
                    this.apiService.postServicePWAData(pwaModel).pipe(
                      tap(() => {
                        // Upload succeeded, remove from pending
                        this.idb
                          .getPendingPWADataByJobAddressId(
                            pwaModel.jobAddressId
                          )
                          .subscribe({
                            next: (pendingData) => {
                              if (pendingData) {
                                this.idb
                                  .removePendingPWAData(
                                    pendingData.pwaBackUpId!
                                  )
                                  .subscribe();
                              }
                            },
                            error: (err) => {
                              console.error(
                                'Error removing pending PWA data:',
                                err
                              );
                            },
                          });
                      }),
                      catchError(
                        (err: HttpErrorResponse, caught: Observable<any>) => {
                          console.error('Upload failed', err);

                          let retryInfo: RetryInfo = {
                            retryCount,
                            retryError: JSON.stringify(err),
                            retryTimeStamp: new Date(),
                          };
                          pwaModel.retryInfo = retryInfo;

                          if (
                            err.error?.detail ===
                            'Duplicate Sync Record Detected.'
                          ) {
                            console.log('Duplicate Sync Record Detected.');
                            return of(this.idb.addPWABackUpData(pwaModel));
                          }

                          if (
                            String(err.error?.detail).includes('deadlocked')
                          ) {
                            if (retryCount === 0) {
                              retryCount++;
                              console.log('Deadlock detected. Retrying...');
                              return caught.pipe(retry(1));
                            }
                          }

                          // Log failed model to backup
                          return of(this.idb.addPWABackUpData(pwaModel));
                        }
                      )
                    )
                  )
                );
              }),
              toArray()
            );
          })
        );
      })
    );

    const calendarNotes$ = this.idb.getAllModifiedCalendarNotes();

    const calendarNotesPost$ = calendarNotes$.pipe(
      concatMap((calendarNotes: CalendarNote[]) => {
        return from(calendarNotes).pipe(
          concatMap((calendarNote: CalendarNote) => {
            return this.apiService.postPWACalendarNote(calendarNote).pipe(
              retry(1),
              catchError((error) => {
                console.error('Error posting calendar note:', error);
                return of(error);
              })
            );
          })
        );
      })
    );

    const syncAttachmentsStatus$ = this.updateSyncStatus({
      syncId: 1,
      syncStatus: 'Syncing Attachments',
      lastSynced: new Date(),
    });

    let concat$ = concat(
      initialStatusUpdate$,
      // pwaModels$,
      postPwaModels$,
      calendarNotesPost$,
      syncAttachmentsStatus$
    );

    return concat$;
  }

  //#endregion

  //#region Sync Tables Sales
  syncSalesCalendar(
    userId: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const salesCalendar = this.apiService.getPWASalesCalendar(userId, hotQuote);

    return salesCalendar.pipe(
      map((salesCalendarEvents) => {
        return salesCalendarEvents.forEach((salesCalendarEvent) => {
          return this.idb.addSalesCalendarEvent(salesCalendarEvent, true);
        });
      })
    );
  }

  syncCustomers(userId: string, hotQuote: boolean = false): Observable<void> {
    const customers = this.apiService.getPWACustomers(userId, hotQuote);

    return customers.pipe(
      map((customers) => {
        return customers.forEach((customer) => {
          return this.idb.addCustomer(customer, true);
        });
      })
    );
  }

  syncJobAddresses(
    userId: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const jobAddresses = this.apiService.getPWAJobAddresses(userId, hotQuote);

    return jobAddresses.pipe(
      map((jobAddresses) => {
        return jobAddresses.forEach((jobAddress) => {
          return this.idb.addJobAddress(jobAddress, true);
        });
      })
    );
  }

  syncSalesAppointments(
    userId: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const appointments = this.apiService.getPWAAppointments(userId, hotQuote);

    return appointments.pipe(
      map((appointments) => {
        return appointments.forEach((appointment) => {
          const ymd = this.helperService.getEffectiveScheduledDate(appointment);
          appointment.effectiveScheduledDate = ymd;
          return this.idb.addAppointment(appointment, true);
        });
      })
    );
  }

  syncLeads(userId: string, hotQuote: boolean = false): Observable<void> {
    const leads = this.apiService.getPWALeads(userId, hotQuote);

    return leads.pipe(
      map((leads) => {
        return leads.forEach((lead) => {
          return this.idb.addLead(lead, true);
        });
      })
    );
  }

  syncCustomerVisits(
    userId: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const customerVisits = this.apiService.getPWACustomerVisits(
      userId,
      hotQuote
    );

    return customerVisits.pipe(
      map((customerVisits) => {
        return customerVisits.forEach((customerVisit) => {
          return this.idb.addCustomerVisit(customerVisit, true);
        });
      })
    );
  }

  syncProposals(userId: string, hotQuote: boolean = false): Observable<void> {
    const proposals = this.apiService.getPWAProposals(userId, hotQuote);

    return proposals.pipe(
      map((proposals) => {
        return proposals.forEach((proposal) => {
          return this.idb.addProposal(proposal, true);
        });
      })
    );
  }

  syncProducts(userId: string, functionName: string): Observable<void> {
    const products = this.apiService.getPWAProducts(userId, functionName);

    return products.pipe(
      map((products) => {
        return products.forEach((product) => {
          return this.idb.addProductCategory(product);
        });
      })
    );
  }

  syncStatements(userId: string, functionName: string): Observable<void> {
    const statements = this.apiService.getPWAStatements(userId, functionName);

    return statements.pipe(
      map((statements) => {
        return statements.forEach((statement) => {
          return this.idb.addStatement(statement);
        });
      })
    );
  }

  syncInsideSalesStatements(): Observable<void> {
    const statements = this.apiService.getInsideSalesStatements();

    return statements.pipe(
      map((statements) => {
        return statements.forEach((statement) => {
          return this.idb.addStatement(statement);
        });
      })
    );
  }

  syncInsideSalesDispositions(): Observable<void> {
    const dispositions = this.apiService.getInsideSalesDispositions();

    return dispositions.pipe(
      map((dispositions) => {
        return dispositions.forEach((disposition) => {
          return this.idb.addDisposition(disposition);
        });
      })
    );
  }

  syncInsideSalesSubDispositions(): Observable<void> {
    const subDispositions = this.apiService.getInsideSalesSubDispositions();

    return subDispositions.pipe(
      map((subDispositions) => {
        return subDispositions.forEach((subDisposition) => {
          return this.idb.addSubDisposition(subDisposition);
        });
      })
    );
  }

  syncInsideSalesNoBidReasons(): Observable<void> {
    const noBidReasons = this.apiService.getInsideSalesNoBidReasons();

    return noBidReasons.pipe(
      map((noBidReasons) => {
        return noBidReasons.forEach((noBidReason) => {
          return this.idb.addNoBidReason(noBidReason);
        });
      })
    );
  }

  syncContracts(userId: string, hotQuote: boolean = false): Observable<void> {
    const contracts = this.apiService.getPWAContracts(userId, hotQuote);

    return contracts.pipe(
      map((contracts) => {
        return contracts.forEach((contract) => {
          return this.idb.addContract(contract, true);
        });
      })
    );
  }

  syncGeneratedContracts(
    userId: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const contracts = this.apiService.getPWAGeneratedContracts(
      userId,
      hotQuote
    );

    return contracts.pipe(
      map((contracts) => {
        return contracts.forEach((contract) => {
          return this.idb.addGeneratedContract(contract, true);
        });
      })
    );
  }

  syncNotes(userId: string, hotQuote: boolean = false): Observable<void> {
    const notes = this.apiService.getPWANotes(userId, hotQuote);

    return notes.pipe(
      map((notes) => {
        return notes.forEach((note) => {
          return this.idb.addNote(note, true);
        });
      })
    );
  }

  syncSoldSheets(userId: string, hotQuote: boolean = false): Observable<void> {
    const soldSheets = this.apiService.getPWASoldSheets(userId, hotQuote);

    return soldSheets.pipe(
      map((soldSheets) => {
        return soldSheets.forEach((soldSheet) => {
          return this.idb.addSoldSheet(soldSheet, true);
        });
      })
    );
  }

  // syncAttachments(userId: string): Observable<void> {
  //   const attachments = this.apiService.getPWAAttachments(userId);

  //   return attachments.pipe(
  //     map((attachments) => {
  //       return attachments.forEach((attachment) => {
  //         return this.idb.addAttachment(attachment);
  //       });
  //     })
  //   );
  // }

  //#endregion

  //#region Sync Tables Production
  syncProdCustomers(userId: string): Observable<void> {
    const customers = this.apiService.getProdPWACustomers(userId);

    return customers.pipe(
      map((customers) => {
        return customers.forEach((customer) => {
          return this.idb.addCustomer(customer, true);
        });
      })
    );
  }

  syncProdJobAddresses(userId: string): Observable<void> {
    const jobAddresses = this.apiService.getProdPWAJobAddresses(userId);

    return jobAddresses.pipe(
      map((jobAddresses) => {
        return jobAddresses.forEach((jobAddress) => {
          return this.idb.addJobAddress(jobAddress, true);
        });
      })
    );
  }

  syncProdAppointments(userId: string): Observable<void> {
    const appointments = this.apiService.getProdPWAAppointments(userId);

    return appointments.pipe(
      map((appointments) => {
        return appointments.forEach((appointment) => {
          const ymd = this.helperService.getEffectiveScheduledDate(appointment);
          appointment.effectiveScheduledDate = ymd;
          return this.idb.addProdAppointment(appointment, true);
        });
      })
    );
  }

  syncProdAvailability(userId: string): Observable<void> {
    const availability = this.apiService.getProdPWAAvailability(userId);

    return availability.pipe(
      map((availability) => {
        return availability.forEach((availability) => {
          return this.idb.addProdAvailability(availability, true);
        });
      })
    );
  }

  syncProdGeneratedContracts(userId: string): Observable<void> {
    const contracts = this.apiService.getProdPWAGeneratedContracts(userId);

    return contracts.pipe(
      map((generatedContracts) => {
        return generatedContracts.forEach((contract) => {
          return this.idb.addGeneratedContract(contract, true);
        });
      })
    );
  }

  syncProdNotes(userId: string): Observable<void> {
    const notes = this.apiService.getProdPWANotes(userId);

    return notes.pipe(
      map((notes) => {
        return notes.forEach((note) => {
          return this.idb.addNote(note, true);
        });
      })
    );
  }

  // syncProdAttachments(userId: string): Observable<void> {
  //   const attachments = this.apiService.getProdPWAAttachments(userId);

  //   return attachments.pipe(
  //     map((attachments) => {
  //       return attachments.forEach((attachment) => {
  //         return this.idb.addAttachment(attachment);
  //       });
  //     })
  //   );
  // }

  syncProdProjects(userId: string): Observable<void> {
    const projects = this.apiService.getProdPWAProjects(userId);

    return projects.pipe(
      map((projects) => {
        return projects.forEach((project) => {
          return this.idb.addProject(project, true);
        });
      })
    );
  }

  syncProdProduction(userId: string): Observable<void> {
    const production = this.apiService.getProdPWAProduction(userId);

    return production.pipe(
      map((production) => {
        return production.forEach((prod) => {
          return this.idb.addProductionVisit(prod, true);
        });
      })
    );
  }

  // syncProdProducts(userId: string): Observable<void> {
  //   const products = this.apiService.getProdPWAProducts(userId);

  //   return products.pipe(
  //     map((products) => {
  //       return products.forEach((product) => {
  //         return this.idb.addProductCategory(product);
  //       });
  //     })
  //   );
  // }

  // syncProdStatements(userId: string): Observable<void> {
  //   const statements = this.apiService.getProdPWAStatements(userId);

  //   return statements.pipe(
  //     map((statements) => {
  //       return statements.forEach((statement) => {
  //         return this.idb.addStatement(statement);
  //       });
  //     })
  //   );
  // }

  syncProdProposals(userId: string): Observable<void> {
    const proposals = this.apiService.getProdPWAProposals(userId);

    return proposals.pipe(
      map((proposals) => {
        return proposals.forEach((proposal) => {
          return this.idb.addProposal(proposal, true);
        });
      })
    );
  }

  syncProdChangeOrders(userId: string): Observable<void> {
    const changeOrders = this.apiService.getProdPWAChangeOrders(userId);

    return changeOrders.pipe(
      map((changeOrders) => {
        return changeOrders.forEach((changeOrder) => {
          return this.idb.addChangeOrder(changeOrder, true);
        });
      })
    );
  }

  syncProdAccountingInfo(userId: string): Observable<void> {
    const accountingInfo = this.apiService.getProdPWAAccountingInfo(userId);

    return accountingInfo.pipe(
      map((accountingInfo) => {
        return accountingInfo.forEach((accountingInfo) => {
          return this.idb.addAccountingInfo(accountingInfo, true);
        });
      })
    );
  }

  syncProdContracts(userId: string): Observable<void> {
    const contracts = this.apiService.getProdPWAContracts(userId);

    return contracts.pipe(
      map((contracts) => {
        return contracts.forEach((contract) => {
          return this.idb.addContract(contract, true);
        });
      })
    );
  }

  syncProdInvoices(userId: string): Observable<void> {
    const invoices = this.apiService.getProdPWAInvoices(userId);

    return invoices.pipe(
      map((invoices) => {
        return invoices.forEach((invoice) => {
          return this.idb.addInvoice(invoice, true);
        });
      })
    );
  }

  // Need prodAvailavility, prodAppointments, projects, production, generatedContracts

  //#endregion

  //#region Sync Tables Service
  syncServiceCustomers(userId: string): Observable<void> {
    const customers = this.apiService.getServicePWACustomers(userId);

    return customers.pipe(
      map((customers) => {
        // console.log('Service Customers', customers);
        return customers.forEach((customer) => {
          return this.idb.addCustomer(customer, true);
        });
      })
    );
  }

  syncServiceJobAddresses(userId: string): Observable<void> {
    const jobAddresses = this.apiService.getServicePWAJobAddresses(userId);

    return jobAddresses.pipe(
      map((jobAddresses) => {
        // console.log('Service Job Addresses', jobAddresses);
        return jobAddresses.forEach((jobAddress) => {
          return this.idb.addJobAddress(jobAddress, true);
        });
      })
    );
  }

  syncServiceAppointments(userId: string): Observable<void> {
    const appointments = this.apiService.getServicePWAAppointments(userId);

    return appointments.pipe(
      map((appointments) => {
        // console.log('Service Appointments', appointments);
        return appointments.forEach((appointment) => {
          const ymd = this.helperService.getEffectiveScheduledDate(appointment);
          appointment.effectiveScheduledDate = ymd;
          return this.idb.addServiceAppointment(appointment, true);
        });
      })
    );
  }

  syncServiceAvailability(userId: string): Observable<void> {
    const availability = this.apiService.getServicePWAAvailability(userId);

    return availability.pipe(
      map((availability) => {
        // console.log('Service Availability', availability);
        return availability.forEach((availability) => {
          return this.idb.addServiceAvailability(availability, true);
        });
      })
    );
  }

  syncServiceContracts(userId: string): Observable<void> {
    const contracts = this.apiService.getServicePWAContracts(userId);

    return contracts.pipe(
      map((contracts) => {
        // console.log('Service Contracts', contracts);
        return contracts.forEach((contract) => {
          return this.idb.addContract(contract, true);
        });
      })
    );
  }

  syncServiceGeneratedContracts(userId: string): Observable<void> {
    const contracts = this.apiService.getServicePWAGeneratedContracts(userId);

    return contracts.pipe(
      map((generatedContracts) => {
        // console.log('Service Generated Contracts', generatedContracts);
        return generatedContracts.forEach((contract) => {
          return this.idb.addGeneratedContract(contract, true);
        });
      })
    );
  }

  syncServiceNotes(userId: string): Observable<void> {
    const notes = this.apiService.getServicePWANotes(userId);

    return notes.pipe(
      map((notes) => {
        // console.log('Service Notes', notes);
        return notes.forEach((note) => {
          return this.idb.addNote(note, true);
        });
      })
    );
  }

  syncServiceAttachments(userId: string): Observable<void> {
    const attachments = this.apiService.getServicePWAAttachments(userId);

    return attachments.pipe(
      map((attachments) => {
        // console.log('Service Attachments', attachments);
        return attachments.forEach((attachment) => {
          return this.idb.addAttachment(attachment, true);
        });
      })
    );
  }

  syncServiceProjects(userId: string): Observable<void> {
    const projects = this.apiService.getServicePWAServiceProjects(userId);

    return projects.pipe(
      map((projects) => {
        // console.log('Service Projects', projects);
        return projects.forEach((project) => {
          return this.idb.addServiceProject(project, true);
        });
      })
    );
  }

  // syncServiceProducts(userId: string): Observable<void> {
  //   const products = this.apiService.getServicePWAProducts(userId);

  //   return products.pipe(
  //     map((products) => {
  //       // console.log('Service Products', products);
  //       return products.forEach((product) => {
  //         return this.idb.addProductCategory(product);
  //       });
  //     })
  //   );
  // }

  // syncServiceStatements(userId: string): Observable<void> {
  //   const statements = this.apiService.getServicePWAStatements(userId);

  //   return statements.pipe(
  //     map((statements) => {
  //       // console.log('Service Statements', statements);
  //       return statements.forEach((statement) => {
  //         return this.idb.addStatement(statement);
  //       });
  //     })
  //   );
  // }

  syncServiceVisits(userId: string): Observable<void> {
    const visits = this.apiService.getServicePWAVisits(userId);

    return visits.pipe(
      map((visits) => {
        // console.log('Service Visits', visits);
        return visits.forEach((visit) => {
          return this.idb.addServiceVisit(visit, true);
        });
      })
    );
  }

  syncServiceProposals(userId: string): Observable<void> {
    const proposals = this.apiService.getServicePWAProposals(userId);

    return proposals.pipe(
      map((proposals) => {
        // console.log('Service Proposals', proposals);
        return proposals.forEach((proposal) => {
          return this.idb.addProposal(proposal, true);
        });
      })
    );
  }

  syncServiceProdProjects(userId: string): Observable<void> {
    const projects = this.apiService.getServicePWAProdProjects(userId);

    return projects.pipe(
      map((projects) => {
        // console.log('Service Production Projects', projects);
        return projects.forEach((project) => {
          return this.idb.addProject(project, true);
        });
      })
    );
  }

  syncServiceProdWrapUps(userId: string): Observable<void> {
    const wrapUps = this.apiService.getServicePWAWrapUps(userId);

    return wrapUps.pipe(
      map((wrapUps) => {
        // console.log('Service Production Wrap Ups', wrapUps);
        return wrapUps.forEach((wrapUp) => {
          return this.idb.addWrapUp(wrapUp, true);
        });
      })
    );
  }

  syncServiceProdVisits(userId: string): Observable<void> {
    const visits = this.apiService.getServicePWAProdVisits(userId);

    return visits.pipe(
      map((visits) => {
        // console.log('Service Production Visits', visits);
        return visits.forEach((visit) => {
          return this.idb.addProductionVisit(visit, true);
        });
      })
    );
  }
  // #endregion

  // General
  syncAllGeoZones(): Observable<void> {
    const geoZones = this.apiService.getAllGeoZones();

    return geoZones.pipe(
      map((geoZones) => {
        // console.log('Geo Zones', geoZones);
        return geoZones.forEach((geoZone) => {
          return this.idb.addGeoZonesFull(geoZone);
        });
      })
    );
  }

  syncUpsellProducts(userId: string, functionName: string): Observable<void> {
    const upsellProducts = this.apiService.getUpsellProducts(
      userId,
      functionName
    );

    return upsellProducts.pipe(
      map((upsellProducts) => {
        // console.log('Upsell Products', upsellProducts);
        return upsellProducts.forEach((upsellProduct) => {
          return this.idb.addUpsellProductSubCategory(upsellProduct);
        });
      })
    );
  }

  syncChangeOrderContracts(userId: string): Observable<void> {
    const contracts = this.apiService.getProdPWAChangeOrderContracts(userId);

    return contracts.pipe(
      map((contracts) => {
        return contracts.forEach((contract) => {
          return this.idb.addChangeOrderContract(contract, true);
        });
      })
    );
  }

  syncGeneratedChangeOrderContracts(userId: string): Observable<void> {
    const contracts =
      this.apiService.getProdPWAGeneratedChangeOrderContracts(userId);

    return contracts.pipe(
      map((contracts) => {
        return contracts.forEach((contract) => {
          return this.idb.addGeneratedChangeOrderContract(contract, true);
        });
      })
    );
  }

  syncCalendarNotes(userId: string, functionName: string): Observable<void> {
    const notes = this.apiService.getPWACalendarNotes(userId, functionName);

    return notes.pipe(
      map((notes) => {
        // console.log('Calendar Notes', notes);
        return notes.forEach((note) => {
          return this.idb.addCalendarNote(note);
        });
      }),
      retry(3)
    );
  }

  syncFollowUps(userId: string, hotQuote: boolean = false): Observable<void> {
    const followUps = this.apiService.getPWAFollowUps(userId, hotQuote);

    return followUps.pipe(
      map((followUps) => {
        return followUps.forEach((followUp) => {
          return this.idb.addFollowUp(followUp, true);
        });
      }),
      retry(3)
    );
  }

  syncSalesContactMethods(
    userId: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const contactMethods = this.apiService.getSalesPWAContactMethods(
      userId,
      hotQuote
    );

    return contactMethods.pipe(
      map((contactMethods) => {
        return contactMethods.forEach((contactMethod) => {
          return this.idb.addContactMethod(contactMethod, true);
        });
      }),
      retry(3)
    );
  }

  syncProdContactMethods(userId: string): Observable<void> {
    const contactMethods = this.apiService.getProdPWAContactMethods(userId);

    return contactMethods.pipe(
      map((contactMethods) => {
        return contactMethods.forEach((contactMethod) => {
          return this.idb.addContactMethod(contactMethod, true);
        });
      }),
      retry(3)
    );
  }

  syncServiceContactMethods(userId: string): Observable<void> {
    const contactMethods = this.apiService.getServicePWAContactMethods(userId);

    return contactMethods.pipe(
      map((contactMethods) => {
        // console.log('Service Contact Methods', contactMethods);
        return contactMethods.forEach((contactMethod) => {
          return this.idb.addContactMethod(contactMethod, true);
        });
      }),
      retry(3)
    );
  }

  syncServiceSoldSheets(userId: string): Observable<void> {
    const soldSheets = this.apiService.getServicePWASoldSheets(userId);

    return soldSheets.pipe(
      map((soldSheets) => {
        // console.log('Service Sold Sheets', soldSheets);
        return soldSheets.forEach((soldSheet) => {
          return this.idb.addSoldSheet(soldSheet, true);
        });
      }),
      retry(3)
    );
  }

  syncNoBidReasons(userId: string, functionName: string): Observable<void> {
    const noBidReasons = this.apiService.getAllNoBidReasons(
      userId,
      functionName
    );

    return noBidReasons.pipe(
      map((noBidReasons) => {
        return noBidReasons.forEach((noBidReason) => {
          return this.idb.addNoBidReason(noBidReason);
        });
      }),
      retry(3)
    );
  }

  syncDispositions(userId: string, functionName: string): Observable<void> {
    const dispositions = this.apiService.getAllDispositions(
      userId,
      functionName
    );

    return dispositions.pipe(
      map((dispositions) => {
        return dispositions.forEach((disposition) => {
          return this.idb.addDisposition(disposition);
        });
      }),
      retry(3)
    );
  }

  syncSubDispositions(userId: string, functionName: string): Observable<void> {
    const subDispositions = this.apiService.getAllSubDispositions(
      userId,
      functionName
    );

    return subDispositions.pipe(
      map((subDispositions) => {
        return subDispositions.forEach((subDisposition) => {
          return this.idb.addSubDisposition(subDisposition);
        });
      }),
      retry(3)
    );
  }

  syncMailingRequests(
    userId: string,
    functionName: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const mailingRequests = this.apiService.getPWAMailingRequests(
      userId,
      functionName,
      hotQuote
    );

    return mailingRequests.pipe(
      map((mailingRequests) => {
        // console.log('Mailing Requests', mailingRequests);
        return mailingRequests.forEach((mailingRequest) => {
          return this.idb.addMailingRequests(mailingRequest, true);
        });
      }),
      retry(3)
    );
  }

  syncDrawings(
    userId: string,
    functionName: string,
    hotQuote: boolean = false
  ): Observable<void> {
    const drawings = this.apiService.getPWADrawings(
      userId,
      functionName,
      hotQuote
    );

    return drawings.pipe(
      map((drawings) => {
        // console.log('Drawings', drawings);
        return drawings.forEach((drawing) => {
          return this.idb.addDrawing(drawing, true);
        });
      }),
      retry(3)
    );
  }

  syncProductPricing(userId: string, functionName: string): Observable<void> {
    const productPricing = this.apiService.getPWAProductPricing(
      userId,
      functionName
    );

    return productPricing.pipe(
      map((productPricing) => {
        console.log('Product Pricing', productPricing);
        return productPricing.forEach((pricing) => {
          return this.idb.addProductPricing(pricing);
        });
      }),
      retry(3)
    );
  }

  syncSpectrumCrewMembers() {
    const crewMembers = this.apiService.getAllSpectrumCrewMembers();

    return crewMembers.pipe(
      map((crewMembers) => {
        return crewMembers.forEach((crewMember) => {
          return this.idb.addSpectrumCrewMember(crewMember);
        });
      }),
      retry(3)
    );
  }

  syncSpectrumMaterials() {
    const materials = this.apiService.getAllSpectrumMaterials();
    return materials.pipe(
      map((materials) => {
        return materials.forEach((material) => {
          return this.idb.addSpectrumMaterial(material);
        });
      }),
      retry(3)
    );
  }

  syncProdDailies(userId: string) {
    const prodDailies = this.apiService.getProdPWAProdDailies(userId);
    return prodDailies.pipe(
      map((prodDailies) => {
        return prodDailies.forEach((daily) => {
          return this.idb.addProdDaily(daily, true);
        });
      }),
      retry(3)
    );
  }

  syncCrews(userId: string) {
    const crews = this.apiService.getProdPWACrews(userId);
    return crews.pipe(
      map((crews) => {
        return crews.forEach((crew) => {
          return this.idb.addCrew(crew, true);
        });
      }),
      retry(3)
    );
  }

  syncTimeLogs(userId: string) {
    const timeLogs = this.apiService.getProdPWATimeLogs(userId);
    return timeLogs.pipe(
      map((timeLogs) => {
        return timeLogs.forEach((timeLog) => {
          return this.idb.addTimeLog(timeLog, true);
        });
      }),
      retry(3)
    );
  }

  syncTimeBreakDowns(userId: string) {
    const timeBreakDowns = this.apiService.getProdPWATimeBreakDowns(userId);
    return timeBreakDowns.pipe(
      map((timeBreakDowns) => {
        return timeBreakDowns.forEach((timeBreakDown) => {
          return this.idb.addTimeBreakDown(timeBreakDown, true);
        });
      }),
      retry(3)
    );
  }

  syncDailyMaterials(userId: string) {
    const dailyMaterials = this.apiService.getProdPWADailyMaterials(userId);
    return dailyMaterials.pipe(
      map((dailyMaterials) => {
        return dailyMaterials.forEach((dailyMaterial) => {
          return this.idb.addDailyMaterial(dailyMaterial, true);
        });
      }),
      retry(3)
    );
  }

  syncDailyPaymentInfo(userId: string) {
    const dailyPaymentInfo = this.apiService.getProdPWADailyPaymentInfo(userId);
    return dailyPaymentInfo.pipe(
      map((dailyPaymentInfo) => {
        return dailyPaymentInfo.forEach((paymentInfo) => {
          return this.idb.addDailyPaymentInfo(paymentInfo, true);
        });
      }),
      retry(3)
    );
  }

  syncDailyPrompts(userId: string) {
    const dailyPrompts = this.apiService.getProdPWADailyPrompts(userId);
    return dailyPrompts.pipe(
      map((dailyPrompts) => {
        return dailyPrompts.forEach((dailyPrompt) => {
          return this.idb.addDailyPrompt(dailyPrompt, true);
        });
      }),
      retry(3)
    );
  }

  syncProdPWAUpsells(userId: string) {
    const upsells = this.apiService.getProdPWAUpsells(userId);
    return upsells.pipe(
      map((upsells) => {
        // console.log('upsells', upsells);
        return upsells.forEach((upsell) => {
          return this.idb.addUpsell(upsell, true);
        });
      }),
      retry(3)
    );
  }

  syncProdPWAUpsellContracts(userId: string) {
    const upsellContracts = this.apiService.getProdPWAUpsellContracts(userId);
    return upsellContracts.pipe(
      map((upsellContracts) => {
        // console.log('upsellContracts', upsellContracts);
        return upsellContracts.forEach((upsellContract) => {
          return this.idb.addUpsellContract(upsellContract, true);
        });
      }),
      retry(3)
    );
  }

  syncProdPWAGeneratedUpsellContracts(userId: string) {
    const generatedUpsellContracts =
      this.apiService.getProdPWAGeneratedUpsellContracts(userId);
    return generatedUpsellContracts.pipe(
      map((generatedUpsellContracts) => {
        // console.log('generatedUpsellContracts', generatedUpsellContracts);
        return generatedUpsellContracts.forEach((generatedUpsellContract) => {
          return this.idb.addGeneratedUpsellContract(
            generatedUpsellContract,
            true
          );
        });
      }),
      retry(3)
    );
  }

  syncProdLeads(userId: string) {
    const leads = this.apiService.getProdPWALeads(userId);
    return leads.pipe(
      map((leads) => {
        // console.log('Prod Leads', leads);
        return leads.forEach((lead) => {
          return this.idb.addLead(lead, true);
        });
      }),
      retry(3)
    );
  }

  syncGeneratedInvoices(userId: string) {
    const generatedInvoices =
      this.apiService.getProdPWAGeneratedInvoices(userId);
    return generatedInvoices.pipe(
      map((generatedInvoices) => {
        return generatedInvoices.forEach((generatedInvoice) => {
          return this.idb.addGeneratedInvoice(generatedInvoice, true);
        });
      }),
      retry(3)
    );
  }

  syncBillingStatements(userId: string) {
    const billingStatements =
      this.apiService.getProdPWABillingStatements(userId);
    return billingStatements.pipe(
      map((billingStatements) => {
        return billingStatements.forEach((billingStatement) => {
          return this.idb.addBillingStatement(billingStatement, true);
        });
      }),
      retry(3)
    );
  }
}

//#endregion
